import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutationState } from '@tanstack/react-query';
import { paymentMutationKeys } from '../../../domain/Payments';
import { PostingStatus, useGetPostings } from '../../../domain/Postings';
import { useModal } from '../../ModalQueue';
import { ModalOrder } from '../../ModalQueue/modalOrder';
import { useRetryBillingPayment } from './useRetryBillingPayment';

const SUSPENDED_POSTINGS_PAYMENT_MUTATION_KEYS = new Set<string>([
  paymentMutationKeys.addCard[0],
  paymentMutationKeys.connectPlaid[0],
  paymentMutationKeys.selectPaymentMethod[0],
  paymentMutationKeys.deleteDefaultPaymentMethod[0],
]);

export const useSuspendedPostingsPopup = () => {
  const [isOpen, setIsOpen] = useState(false);

  const { isActive: isModalActive, hide: hideModal } = useModal({
    id: ModalOrder.SUSPENDED_POSTINGS_POPUP,
    condition: isOpen,
  });

  const paymentMutationSuccessSubmittedAt = useMutationState<number>({
    filters: {
      status: 'success',
      predicate: (mutation) =>
        SUSPENDED_POSTINGS_PAYMENT_MUTATION_KEYS.has(
          String(mutation.options.mutationKey?.[0] ?? ''),
        ),
    },
    select: (mutation) => mutation.state.submittedAt,
  });

  const latestPaymentMutationSuccessSubmittedAt = useMemo(
    () => Math.max(0, ...paymentMutationSuccessSubmittedAt),
    [paymentMutationSuccessSubmittedAt],
  );
  const handledPaymentMutationSuccessSubmittedAtRef = useRef(
    latestPaymentMutationSuccessSubmittedAt,
  );

  const {
    data: postings,
    pagination,
    isLoading,
    refetch,
  } = useGetPostings(
    {
      page: 1,
      limit: 15,
      status: PostingStatus.SUSPENDED,
    },
    { enabled: false },
  );

  const totalCount = pagination?.total || 0;

  const {
    paymentResultState,
    isProcessing,
    failedPostingIds,
    handleRetryBilling,
    resetPaymentState,
  } = useRetryBillingPayment();

  useEffect(() => {
    if (
      !latestPaymentMutationSuccessSubmittedAt ||
      latestPaymentMutationSuccessSubmittedAt <= handledPaymentMutationSuccessSubmittedAtRef.current
    ) {
      return;
    }

    handledPaymentMutationSuccessSubmittedAtRef.current = latestPaymentMutationSuccessSubmittedAt;

    if (isOpen) {
      return;
    }

    refetch().then((result) => {
      const hasPostings = (result.data?.data?.length ?? 0) > 0;
      if (hasPostings) {
        setIsOpen(true);
      }
    });
  }, [isOpen, latestPaymentMutationSuccessSubmittedAt, refetch]);

  const failedPostings = useMemo(() => {
    if (failedPostingIds.length === 0) {
      return [];
    }
    return postings.filter((posting) => failedPostingIds.includes(posting.id));
  }, [postings, failedPostingIds]);

  const handlePayNow = async () => {
    const result = await handleRetryBilling();
    if (result) {
      setIsOpen(false);
      hideModal();
    }
  };

  const handlePayLater = () => {
    setIsOpen(false);
    hideModal();
  };

  const handleCloseSuccessPopup = () => {
    resetPaymentState();
    refetch();
  };

  const handleRetryFromFailedPopup = () => {
    resetPaymentState();
    handlePayNow();
  };

  const handlePayLaterFromFailedPopup = () => {
    resetPaymentState();
  };

  return {
    isOpen: isModalActive,
    suspendedPostings: postings,
    failedPostings,
    totalCount,
    isLoading,
    isProcessing,
    paymentResultState,
    handlePayNow,
    handlePayLater,
    handleCloseSuccessPopup,
    handleRetryFromFailedPopup,
    handlePayLaterFromFailedPopup,
  };
};
